import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const GlassEffect = `
    background-color: rgba(255, 255, 255, 0.01);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
`;

const DetailPageContainer = styled.div`
    width: 100%;
    max-width: 1000px;
    animation: ${fadeIn} 0.5s ease;
    padding: 20px 0 50px;
`;

const MosaicGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(2, 220px);
    gap: 15px;
    margin-bottom: 30px;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(2, 150px);
    }
`;

const MosaicItem = styled(motion.div)`
    width: 100%;
    height: 100%;
    cursor: zoom-in;
    position: relative;
    overflow: hidden;
    border-radius: 25px;

    &:nth-child(1) {
        grid-column: span 2;
        grid-row: span 2;
        @media (max-width: 768px) {
            grid-row: span 1;
        }
    }

    &:nth-child(2) {
        grid-column: span 2;
        grid-row: span 1;
    }
`;

const MosaicImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    box-shadow: 0 10px 20px rgba(0,0,0,0.1);
`;

const InfoGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 30px;
    ${GlassEffect}
    padding: 40px;
    border-radius: 30px;
    background: rgba(255, 255, 255, 0.4);

    @media (max-width: 768px) {
        padding: 20px;
        gap: 20px;
    }
`;

const TwoColumnGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const InfoItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    border-bottom: 1px solid rgba(191, 145, 156, 0.1);
    padding-bottom: 15px;

    &:last-child {
        border-bottom: none;
    }
`;

const InfoLabel = styled.span`
    font-size: 0.85rem;
    color: #bf919c;
    text-transform: uppercase;
    font-weight: 800;
    font-family: unbounded, sans-serif;
    letter-spacing: 1px;
`;

const InfoValue = styled.span`
    font-size: 1.1rem;
    color: #444;
    font-family: unbounded, sans-serif;
    line-height: 1.5;
`;

const InfoValueWrapper = styled.div`
    white-space: pre-line;
`;

const ExtraInfoValueWrapper = styled.div`
    white-space: pre-line;
    color: #8b0000;
`;

const CurrencyWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const CurrencyButton = styled(motion.div)`
    width: 28px;
    height: 28px;
    background: #bf919c;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 0.8rem;
    cursor: pointer;
    font-weight: bold;
    user-select: none;
    box-shadow: 0 2px 5px rgba(191, 145, 156, 0.3);

    &:hover {
        background: #a87b87;
    }
`;

const ListValue = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ListValueItem = styled.li`
    font-size: 1.1rem;
    color: #444;
    font-family: unbounded, sans-serif;
    display: flex;
    align-items: flex-start;

    &:before {
        content: "•";
        color: #bf919c;
        font-weight: bold;
        margin-right: 10px;
    }
`;

const MapWrapper = styled.div`
    width: 100%;
    height: 300px;
    border-radius: 20px;
    overflow: hidden;
    margin-top: 20px;
    border: 2px solid rgba(191, 145, 156, 0.2);
    z-index: 1;
`;

const FullscreenImageWrapper = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0,0,0,0.95);
    z-index: 2000;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: zoom-out;
`;

const FullscreenImage = styled(motion.img)`
    max-width: 95%;
    max-height: 95%;
    border-radius: 10px;
    object-fit: contain;
`;

const FullscreenCloseButton = styled.button`
    position: absolute;
    top: 30px;
    right: 40px;
    background: none;
    border: none;
    font-size: 40px;
    cursor: pointer;
    color: white;
    z-index: 2001;

    @media (max-width: 768px) {
        top: 15px;
        right: 20px;
        font-size: 30px;
    }
`;

const ObjectDetails = ({ selectedObject, currency, toggleCurrency, rates }) => {
    const [expandedImage, setExpandedImage] = useState(null);

    const formatPrice = (priceString) => {
        if (!priceString) return '';
        const numericPrice = parseInt(priceString.replace(/\s/g, ''));

        if (currency === 'USD') {
            return `${Math.round(numericPrice / rates.USD).toLocaleString()} $`;
        }
        if (currency === 'EUR') {
            return `${Math.round(numericPrice / rates.EUR).toLocaleString()} €`;
        }
        return priceString;
    };

    const getCurrencyIcon = () => {
        if (currency === 'USD') return '$';
        if (currency === 'EUR') return '€';
        return '₴';
    };

    return (
        <DetailPageContainer>
            <MosaicGrid>
                {selectedObject.images.map((img, i) => (
                    <MosaicItem
                        key={i}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => setExpandedImage(img)}
                    >
                        <MosaicImage src={img} alt="object" />
                    </MosaicItem>
                ))}
            </MosaicGrid>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
            >
                <InfoGrid>
                    <InfoItem>
                        <InfoLabel>Назва</InfoLabel>
                        <InfoValue>{selectedObject.name}</InfoValue>
                    </InfoItem>

                    {selectedObject.coords && (
                        <InfoItem>
                            <InfoLabel>Місцезнаходження</InfoLabel>
                            <MapWrapper>
                                <MapContainer
                                    center={selectedObject.coords}
                                    zoom={15}
                                    scrollWheelZoom={false}
                                    style={{ height: '100%', width: '100%' }}
                                >
                                    <TileLayer
                                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    />
                                    <Marker position={selectedObject.coords}>
                                        <Popup>{selectedObject.name}</Popup>
                                    </Marker>
                                </MapContainer>
                            </MapWrapper>
                        </InfoItem>
                    )}

                    <TwoColumnGrid>
                        {selectedObject.minAge && (
                            <InfoItem>
                                <InfoLabel>Мін. вік оренди</InfoLabel>
                                <InfoValue>{selectedObject.minAge}+</InfoValue>
                            </InfoItem>
                        )}
                        {selectedObject.price && (
                            <InfoItem>
                                <InfoLabel>Ціна</InfoLabel>
                                <CurrencyWrapper>
                                    <InfoValue>{formatPrice(selectedObject.price)}</InfoValue>
                                    <CurrencyButton
                                        whileTap={{ scale: 0.9 }}
                                        onClick={toggleCurrency}
                                    >
                                        {getCurrencyIcon()}
                                    </CurrencyButton>
                                </CurrencyWrapper>
                            </InfoItem>
                        )}
                    </TwoColumnGrid>

                    {selectedObject.suitableFor && (
                        <InfoItem>
                            <InfoLabel>Підходить для</InfoLabel>
                            <ListValue>
                                {selectedObject.suitableFor.map((item, idx) => (
                                    <ListValueItem key={idx}>{item}</ListValueItem>
                                ))}
                            </ListValue>
                        </InfoItem>
                    )}

                    {selectedObject.rules && (
                        <InfoItem>
                            <InfoLabel>Правила проживання</InfoLabel>
                            <InfoValueWrapper>
                                <InfoValue>{selectedObject.rules}</InfoValue>
                            </InfoValueWrapper>
                        </InfoItem>
                    )}

                    {selectedObject.extra && (
                        <InfoItem>
                            <InfoLabel>Додаткова інформація</InfoLabel>
                            <ExtraInfoValueWrapper>
                                <InfoValue>{selectedObject.extra}</InfoValue>
                            </ExtraInfoValueWrapper>
                        </InfoItem>
                    )}
                </InfoGrid>
            </motion.div>

            <AnimatePresence>
                {expandedImage && (
                    <FullscreenImageWrapper
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setExpandedImage(null)}
                    >
                        <FullscreenCloseButton onClick={() => setExpandedImage(null)}>
                            &times;
                        </FullscreenCloseButton>
                        <FullscreenImage
                            src={expandedImage}
                            initial={{ scale: 0.8 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.8 }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </FullscreenImageWrapper>
                )}
            </AnimatePresence>
        </DetailPageContainer>
    );
};

export default ObjectDetails;